import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, Input, Output, EventEmitter, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertModule, ButtonModule, CardModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslationModule } from 'i18n';
import { CurrencyAmountPipe, Logger, Platform } from 'shared';
import { PaymentMethod } from '../../../../../models/order.model';
import { OrderWizardService } from '../../../../../services/order-wizard.service';
import { OrderService } from '../../../../../services/order.service';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trackingErrorCategory } from 'analytics';
import { OrderWizardTracking } from '../../order-wizard-tracking';

export type PaymentMethodType = 'ewallet' | 'web3_payment' | 'credit_card' | 'authorize_net';

interface PaymentMethodsConfig {
  enabled_methods: PaymentMethodType[];
  available_methods: PaymentMethodType[];
}

@Component({
  selector: 'app-payment-method',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AlertModule,
    ButtonModule,
    CardModule,
    IconModule,
    TranslationModule,
    CurrencyAmountPipe
  ],
  templateUrl: './payment-method.component.html',
  styleUrls: ['./payment-method.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentMethodComponent {
  @Input() totalPrice = 0;
  @Output() onNext = new EventEmitter<void>();
  @Output() onBack = new EventEmitter<void>();

  protected logger = new Logger('PaymentMethodComponent');
  private orderService = inject(OrderService);
  private wizardService = inject(OrderWizardService);
  private httpClient = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  public  platform = inject(Platform);
  private tracking = inject(OrderWizardTracking, { optional: true });

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  selectedMethod = signal<PaymentMethodType | null>(null);
  loading = signal<boolean>(false);
  error = signal<string>('');
  userBalance = signal<number>(0);
  currency = signal<string>('USDT');
  ewalletAvailable = signal<boolean>(false);
  availablePaymentMethods = signal<PaymentMethod[]>([]);
  enabledPaymentMethods = signal<PaymentMethodType[]>([]);
  paymentCosts = signal<number>(0);
  totalWithCosts = signal<number>(0);

  licenseConfirmed = signal<boolean>(false);

  // Customer-facing surcharge per gateway. Must mirror Order::PAYMENT_SURCHARGE
  // on the backend (which is authoritative — the server recomputes and validates).
  //   credit_card (Stripe): 4.9%  + 0.9
  //   authorize_net:        4.0%  + 0.5
  private static readonly SURCHARGE: Record<string, { rate: number; fixed: number }> = {
    credit_card:   { rate: 0.049, fixed: 0.9 },
    authorize_net: { rate: 0.04,  fixed: 0.5 }
  };

  private surchargeFor(method: PaymentMethodType | null): number {
    const cfg = method ? PaymentMethodComponent.SURCHARGE[method] : undefined;
    if (!cfg) return 0;
    return Math.round((this.totalPrice * cfg.rate + cfg.fixed) * 100) / 100; // 2 dp
  }

  // Computed
  creditCardCosts = computed(() => this.surchargeFor('credit_card'));

  finalAmount = computed(() => this.totalPrice + this.surchargeFor(this.selectedMethod()));

  hasLicenseProducts = computed(() => {
    const state = this.wizardService.getState();
    return state.cart.some(item => item.product.code?.startsWith('LIC_'));
  });

  canContinue = computed(() =>
    !!this.selectedMethod() &&
    !this.loading() &&
    (this.selectedMethod() !== 'ewallet' || this.ewalletAvailable()) &&
    (!this.hasLicenseProducts() || this.licenseConfirmed())
  );

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    // Load payment config & calculation once
    effect(() => {
      this.checkAvailablePaymentMethods();
    });

    // Auto-update costs when method or total changes
    effect(() => {
      this.selectedMethod();
      this.totalPrice;
      this.updatePaymentCosts();
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private checkAvailablePaymentMethods(): void {
    this.loading.set(true);
    this.error.set('');

    const state = this.wizardService.getState();
    const items = state.cart.map(item => ({
      product_id: item.product.id,
      quantity: item.quantity
    }));

    forkJoin({
      calculation: this.orderService.calculateOrder(items),
      paymentConfig: this.httpClient.post<PaymentMethodsConfig>('/config/payment_methods', {}).pipe(
        catchError(() => of({
          enabled_methods: ['ewallet', 'web3_payment', 'credit_card'] as PaymentMethodType[],
          available_methods: [] as PaymentMethodType[]
        }))
      )
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: ({ calculation, paymentConfig }) => {
        this.userBalance.set(calculation.user_balance ?? 0);
        this.currency.set(calculation.currency ?? 'USDT');
        this.ewalletAvailable.set(calculation.ewallet_available ?? false);
        this.availablePaymentMethods.set(calculation.available_payment_methods ?? []);
        this.enabledPaymentMethods.set(paymentConfig.enabled_methods ?? []);
        this.loading.set(false);
      },
      error: err => {
        this.error.set('Failed to load payment options');
        this.loading.set(false);
        this.tracking?.step('payment_method', 'error', trackingErrorCategory(err));
        this.logger.error('Payment config error:', err);
      }
    });
  }

  isMethodEnabled(method: PaymentMethodType): boolean {
    return this.enabledPaymentMethods().includes(method);
  }

  selectMethod(method: PaymentMethodType): void {
    if (method === 'ewallet' && !this.ewalletAvailable()) return;

    this.selectedMethod.set(method);
  }

  private updatePaymentCosts(): void {
    const costs = this.surchargeFor(this.selectedMethod());
    this.paymentCosts.set(costs);
    this.totalWithCosts.set(this.totalPrice + costs);

    this.wizardService.setPaymentCosts(this.paymentCosts(), this.totalWithCosts());
  }

  confirmSelection(): void {
    const method = this.selectedMethod();
    if (!method) {
      this.tracking?.step('payment_method', 'error', 'validation');
      return;
    }

    this.tracking?.step('payment_method', 'submit');
    this.loading.set(true);
    this.error.set('');

    const state = this.wizardService.getState();
    const items = state.cart.map(item => ({
      product_id: item.product.id,
      quantity: item.quantity
    }));

    this.orderService.createOrder({
      items,
      payment_method: method
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        this.wizardService.setOrderId(response.order_id);
        this.wizardService.setPaymentMethod(method);
        this.wizardService.setOrderStatus(response.status);

        if (method === 'web3_payment') {
          this.orderService.initiatePayment(response.order_id, method)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: initResponse => {
                  if (initResponse.payment_details?.recipient_address) {
                    this.wizardService.setRecipientAddress(initResponse.payment_details.recipient_address);
                  }
                  this.loading.set(false);
                  this.onNext.emit();
                },
                error: err => {
                  this.error.set(err.error?.error || 'Failed to initiate Web3 payment');
                  this.loading.set(false);
                  this.tracking?.step('payment_method', 'error', trackingErrorCategory(err));
                }
              });
        } else {
          this.loading.set(false);
          this.onNext.emit();
        }
      },
      error: err => {
        this.error.set(err.error?.error || 'Failed to create order');
        this.loading.set(false);
        this.tracking?.step('payment_method', 'error', trackingErrorCategory(err));
      }
    });
  }

  goBack(): void {
    this.onBack.emit();
  }
}